import TranslatableText from "@components/translations/TranslatableText";
import MSADataChart from "./MSADataChart";
import Skeleton from "react-loading-skeleton";
import { transformMsaToGraphData } from "./utils";
import { useState } from "react";
import { useAppDispatch, useAppSelector } from "@app/hooks";
import { selectCurrentBuilder } from "@app/features/builder/builderSlice";
import { selectMsaJobsByProject } from "@app/features/msa/msaSlice";
import { selectProjectAreaDefined } from "@app/features/project/projectSlice";
import { Tooltip, TooltipContent, TooltipTrigger } from "@components/tooltip/Tooltip";
import { HelpIcon } from "@utils/customIcons";
import { triggerMsaCalculation } from "@app/services/msaApiService";
import { MdRefresh } from "react-icons/md";
import type { RootState } from "@app/store";

interface MsaSectionProps {
  projectId?: string;
}

const MsaSection = ({ projectId }: MsaSectionProps) => {
  const dispatch = useAppDispatch();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const tabsOpen = useAppSelector(selectCurrentBuilder);
  const msaJobs = useAppSelector((state: RootState) => 
    projectId ? selectMsaJobsByProject(state, projectId) : null
  );
  const projectAreaDefined = useAppSelector(selectProjectAreaDefined);

  // Transform the MSA data
  const graphData = transformMsaToGraphData(msaJobs?.baseline ?? null, msaJobs?.project ?? null);
  
  // Determine loading state based on job status
  const isLoading = 
    msaJobs?.baseline?.status === "starting" || 
    msaJobs?.baseline?.status === "in_progress" ||
    msaJobs?.project?.status === "starting" || 
    msaJobs?.project?.status === "in_progress";

  // Check if data is missing
  const hasData = graphData.length > 0;
  const baselineFailed = msaJobs?.baseline?.status === "failed";
  const projectFailed = msaJobs?.project?.status === "failed";
  const hasError = baselineFailed || projectFailed;
  
  // Check which data is available
  const hasBaselineData = msaJobs?.baseline?.result != null;
  const hasProjectData = msaJobs?.project?.result != null;
  const hasPartialData = (hasBaselineData || hasProjectData) && !(hasBaselineData && hasProjectData);
  
  // Get aggregate MSA values for summary boxes
  const finalData = graphData.find(item => item.category === "MSA Aggregate");
  const withoutProjectValue = finalData?.withoutProject;
  const withProjectValue = finalData?.withProject;

  // Refresh button: visible whenever project area is defined
  const showRefreshButton = projectAreaDefined === true;

  const handleRefresh = async () => {
    if (!projectId || isRefreshing || isLoading) return;
    setIsRefreshing(true);
    try {
      await Promise.all([
        triggerMsaCalculation(projectId, "baseline", dispatch),
        triggerMsaCalculation(projectId, "project", dispatch),
      ]);
    } finally {
      setIsRefreshing(false);
    }
  };

  return (
    <div className="msa-container">
      <div className="d-flex align-items-center justify-content-between gap-8 mb-1">
        <h3 className="ff-medium fs-18">
          <TranslatableText translationKey="map.mean_species_abundance" />
        </h3>
        <div className="d-flex align-items-center gap-8">
          {showRefreshButton && (
            <Tooltip placement="bottom">
              <TooltipTrigger asChild>
                <button
                  onClick={handleRefresh}
                  disabled={isRefreshing || isLoading}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'none',
                    border: '1px solid var(--msa-baseline-border)',
                    borderRadius: '6px',
                    padding: '4px 6px',
                    cursor: isRefreshing || isLoading ? 'not-allowed' : 'pointer',
                    opacity: isRefreshing || isLoading ? 0.5 : 1,
                    color: '#555',
                    transition: 'background-color 0.2s, opacity 0.2s',
                  }}
                >
                  <MdRefresh
                    size={16}
                    style={{
                      animation: isRefreshing ? 'rotate 0.8s linear infinite' : 'none',
                    }}
                  />
                </button>
              </TooltipTrigger>
              <TooltipContent  className="msa-info-tooltip">
                <TranslatableText translationKey="map.msa_refresh_results" />
              </TooltipContent>
            </Tooltip>
          )}
          <Tooltip placement="bottom">
            <TooltipTrigger asChild>
              <span style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                <HelpIcon size={18} />
              </span>
            </TooltipTrigger>
            <TooltipContent className="msa-info-tooltip">
              <TranslatableText translationKey="map.mean_species_abundance_description" />
            </TooltipContent>
          </Tooltip>
        </div>
      </div>

      {/* Alert messages for missing or error states */}
      {!isLoading && hasError && (
        <div className="mb-2" style={{ 
          padding: '8px 12px', 
          fontSize: '14px',
          backgroundColor: '#fde8e8',
          border: '1px solid #f5c2c2',
          borderRadius: '6px',
          color: '#9c0004'
        }}>
          {baselineFailed && (
            <div>
              <TranslatableText translationKey="map.msa_calculation_failed" />: {msaJobs?.baseline?.error}
            </div>
          )}
          {projectFailed && (
            <div>
              <TranslatableText translationKey="map.msa_calculation_failed" />: {msaJobs?.project?.error}
            </div>
          )}
        </div>
      )}
      
      {/* Partial Data Info */}
      {!isLoading && !hasError && hasPartialData && (
        <div className="mb-2" style={{ 
          padding: '8px 12px', 
          fontSize: '14px',
          backgroundColor: '#e5ecf4',
          border: '1px solid #b8d4e8',
          borderRadius: '6px',
          color: '#164777'
        }}>
          {!hasProjectData && (
            <TranslatableText translationKey="map.msa_only_baseline_available" />
          )}
          {!hasBaselineData && (
            <TranslatableText translationKey="map.msa_only_project_available" />
          )}
        </div>
      )}
      
      {!isLoading && !hasData && !hasError && (
        <div className="mb-2" style={{ 
          padding: '8px 12px', 
          fontSize: '14px',
          backgroundColor: '#e5ecf4',
          border: '1px solid #b8d4e8',
          borderRadius: '6px',
          color: '#164777'
        }}>
          <TranslatableText translationKey="map.no_msa_results_available" />
        </div>
      )}
      {isLoading && (
        <div className="mb-2" style={{ 
          padding: '8px 12px', 
          fontSize: '14px',
          backgroundColor: '#fff9e6',
          border: '1px solid #ffe8b3',
          borderRadius: '6px',
          color: '#b8860b'
        }}>
          <TranslatableText translationKey="map.msa_calculation_in_progress" />
        </div>
      )}

      {/* Responsive Container */}
      <div className={`msa-responsive-container d-flex gap-4 ${tabsOpen ? 'f-column' : ''}`}>
        {/* Left Column: Summary Boxes and Table */}
        <div className="msa-left-column d-flex f-column gap-4">
          {/* Summary Boxes */}
          <div className="d-flex gap-8">
            {isLoading ? (
              <>
                <div style={{ flex: 1 }}>
                  <Skeleton height={100} borderRadius={8} />
                </div>
                <div style={{ flex: 1 }}>
                  <Skeleton height={100} borderRadius={8} />
                </div>
              </>
            ) : (
              <>
                <div
                  className="msa-value-box"
                  style={{
                    backgroundColor: 'var(--msa-baseline-bg)',
                    border: '1px solid var(--msa-baseline-border)'
                  }}
                >
                  <div className="fs-12" style={{ color: '#666' }}>
                    <TranslatableText translationKey="map.baseline" />
                  </div>
                  <div className="fs-20 ff-medium-ext pt-1">
                    {withoutProjectValue !== null && withoutProjectValue !== undefined 
                      ? withoutProjectValue.toFixed(2) 
                      : 'N/A'}
                  </div>
                </div>
                <div
                  className="msa-value-box"
                  style={{
                    backgroundColor: 'var(--msa-project-bg)',
                    border: '1px solid var(--msa-project-border)'
                  }}
                >
                  <div className="fs-12" style={{ color: '#666' }}>
                    <TranslatableText translationKey="map.with_project" />
                  </div>
                  <div className="fs-20 ff-medium-ext pt-1">
                    {withProjectValue !== null && withProjectValue !== undefined 
                      ? withProjectValue.toFixed(2) 
                      : 'N/A'}
                  </div>
                </div>
              </>
            )}
          </div>


          {/* Detailed Table */}
          <div className="pt-2">
            {isLoading ? (
              <Skeleton height={250} borderRadius={8} />
            ) : hasData ? (
              <MSADataChart data={graphData} />
            ) : (
              <div style={{ padding: '16px', textAlign: 'center', color: '#666' }}>
                <TranslatableText translationKey="map.no_data_available" />
              </div>
            )}
          </div>
        </div>

        {/* Right Column: Radar Chart */}
        <div className={`msa-chart-column w-100 pt-2 ${tabsOpen && !isLoading && hasData ? 'ps-2' : ''}`}>
          {isLoading ? (
            <Skeleton height={300} borderRadius={8} />
          ) : hasData ? (
            <table className="w-100" style={{ borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #e0e0e0' }}>
                  <th className="text-left p-1 fs-12" style={{ fontWeight: 'normal', color: '#666' }}>
                    <TranslatableText translationKey="map.msa_table_header" />
                  </th>
                  <th className="text-center p-1 fs-12" style={{ fontWeight: 'normal', color: '#666' }}>
                    <TranslatableText translationKey="map.baseline" />
                  </th>
                  <th className="text-center p-1 fs-12" style={{ fontWeight: 'normal', color: '#666' }}>
                    <TranslatableText translationKey="map.with_project" />
                  </th>
                </tr>
              </thead>
              <tbody>
                {graphData.map((item, index) => (
                  <tr key={index} style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td className="p-1 fs-14">{item.category}</td>
                    <td className="text-center p-1 fs-14">
                      {item.withoutProject !== null && item.withoutProject !== undefined 
                        ? item.withoutProject.toFixed(4) 
                        : '-'}
                    </td>
                    <td className="text-center p-1 fs-14">
                      {item.withProject !== null && item.withProject !== undefined 
                        ? item.withProject.toFixed(4) 
                        : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div style={{ padding: '16px', textAlign: 'center', color: '#666' }}>
              <TranslatableText translationKey="map.no_data_available" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default MsaSection;